"use client";

import { c as _c } from "react/compiler-runtime";
import { useChains } from "@opensea/app-kit";
import { useCurrency } from "@opensea/navigation";
import { FlexCenter, NumberDisplay, Separator, SkeletonLine } from "@opensea/ui-kit";
import { useAccount, useBalances, useIsEmbeddedWallet } from "@opensea/wallet";
import { useContextSelector } from "use-context-selector";
import { TokenPriceDisplay } from "@/components/price/TokenPrice";
import { AsteriskText } from "@/components/profile/AsteriskText";
import { ProfileSettingsContext } from "@/components/profile/ProfileSettingsProvider";
export function AccountBalance() {
  const $ = _c(38);
  const {
    chainId,
    chainArch
  } = useAccount();
  const {
    getNativeCurrency,
    getOfferCurrency,
    getChainIdentifier
  } = useChains();
  const {
    balances,
    loading,
    getBalanceOfCurrency
  } = useBalances();
  const {
    currency
  } = useCurrency();
  const hideWalletWorth = useContextSelector(ProfileSettingsContext, _temp);
  const isEmbeddedWallet = useIsEmbeddedWallet();
  if (hideWalletWorth) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <AsteriskText length={7} />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] !== currency) {
      t1 = currency === "crypto" ? <><Separator className="mx-3 h-4" orientation="vertical" /><AsteriskText length={7} /></> : null;
      $[1] = currency;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1) {
      t2 = <FlexCenter>{t0}{t1}</FlexCenter>;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!chainId) {
    let t0;
    if ($[5] !== currency) {
      t0 = <AccountBalanceSkeleton currency={currency} />;
      $[5] = currency;
      $[6] = t0;
    } else {
      t0 = $[6];
    }
    return t0;
  }
  let chainIdentifier;
  let nativeBalance;
  let nativeCurrency;
  let offerCurrency;
  let t0;
  if ($[7] !== chainArch || $[8] !== chainId || $[9] !== getBalanceOfCurrency || $[10] !== getChainIdentifier || $[11] !== getNativeCurrency || $[12] !== getOfferCurrency) {
    chainIdentifier = getChainIdentifier(chainId, chainArch) || "ethereum";
    nativeCurrency = getNativeCurrency(chainIdentifier);
    nativeBalance = getBalanceOfCurrency(nativeCurrency);
    offerCurrency = getOfferCurrency(chainIdentifier);
    t0 = getBalanceOfCurrency(offerCurrency);
    $[7] = chainArch;
    $[8] = chainId;
    $[9] = getBalanceOfCurrency;
    $[10] = getChainIdentifier;
    $[11] = getNativeCurrency;
    $[12] = getOfferCurrency;
    $[13] = chainIdentifier;
    $[14] = nativeBalance;
    $[15] = nativeCurrency;
    $[16] = offerCurrency;
    $[17] = t0;
  } else {
    chainIdentifier = $[13];
    nativeBalance = $[14];
    nativeCurrency = $[15];
    offerCurrency = $[16];
    t0 = $[17];
  }
  const offerBalance = t0;
  if (loading) {
    let t1;
    if ($[18] !== currency) {
      t1 = <AccountBalanceSkeleton currency={currency} />;
      $[18] = currency;
      $[19] = t1;
    } else {
      t1 = $[19];
    }
    return t1;
  }
  let t1;
  if ($[20] !== balances || $[21] !== chainIdentifier || $[22] !== currency || $[23] !== isEmbeddedWallet || $[24] !== nativeBalance?.quantity || $[25] !== nativeBalance?.usdValue || $[26] !== nativeCurrency) {
    t1 = currency === "crypto" && !isEmbeddedWallet ? <TokenPriceDisplay animated={true} chain={chainIdentifier} contractAddress={nativeCurrency.contractAddress} symbol={nativeCurrency.symbol} symbolColor="text-primary" unit={Number(nativeBalance?.quantity ?? 0)} usdPrice={Number(nativeBalance?.usdValue ?? 0)} /> : <NumberDisplay display="usd" value={balances.reduce(_temp2, 0)} />;
    $[20] = balances;
    $[21] = chainIdentifier;
    $[22] = currency;
    $[23] = isEmbeddedWallet;
    $[24] = nativeBalance?.quantity;
    $[25] = nativeBalance?.usdValue;
    $[26] = nativeCurrency;
    $[27] = t1;
  } else {
    t1 = $[27];
  }
  let t2;
  if ($[28] !== chainIdentifier || $[29] !== currency || $[30] !== nativeCurrency || $[31] !== offerBalance?.quantity || $[32] !== offerBalance?.usdValue || $[33] !== offerCurrency) {
    t2 = currency === "crypto" && offerCurrency.contractAddress !== nativeCurrency.contractAddress ? <><Separator className="mx-3 h-4" orientation="vertical" /><TokenPriceDisplay animated={true} chain={chainIdentifier} contractAddress={offerCurrency.contractAddress} symbol={offerCurrency.symbol} symbolColor="text-primary" unit={Number(offerBalance?.quantity ?? 0)} usdPrice={Number(offerBalance?.usdValue ?? 0)} /></> : null;
    $[28] = chainIdentifier;
    $[29] = currency;
    $[30] = nativeCurrency;
    $[31] = offerBalance?.quantity;
    $[32] = offerBalance?.usdValue;
    $[33] = offerCurrency;
    $[34] = t2;
  } else {
    t2 = $[34];
  }
  let t3;
  if ($[35] !== t1 || $[36] !== t2) {
    t3 = <FlexCenter>{t1}{t2}</FlexCenter>;
    $[35] = t1;
    $[36] = t2;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  return t3;
}
function _temp2(prev, accum) {
  return prev + Number(accum.usdValue);
}
function _temp(state) {
  return state.hideWalletWorth;
}
function AccountBalanceSkeleton(t0) {
  const $ = _c(5);
  const {
    currency
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkeletonLine className="w-20" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== currency) {
    t2 = currency === "crypto" && <><Separator className="mx-3 h-4" orientation="vertical" /><SkeletonLine className="w-20" /></>;
    $[1] = currency;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <FlexCenter>{t1}{t2}</FlexCenter>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}